import { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import {
  User,
  ShieldCheck,
  ShieldAlert,
  Smartphone,
  Copy,
  Check,
  Download,
  Laptop,
  Trash2,
  MailCheck,
  X,
  KeyRound,
  LogOut,
  Loader2,
  CheckCircle,
  AlertCircle,
  Bell,
} from 'lucide-react';
import { useAuth } from '../stores/authStore';
import { userApi } from '../services/api';
import { toast, confirmDialog } from '../stores/feedbackStore';

interface SessionItem {
  id: string;
  token: string;
  expires_at: string;
  created_at: string;
  ip_address: string | null;
  user_agent: string | null;
}

function parseUserAgent(ua: string | null) {
  if (!ua) return { name: 'Trình duyệt Web', isMobile: false };
  let os = 'Windows';
  let isMobile = false;
  if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Macintosh')) os = 'macOS';
  else if (ua.includes('iPhone')) { os = 'iPhone'; isMobile = true; }
  else if (ua.includes('Android')) { os = 'Android'; isMobile = true; }
  else if (ua.includes('Linux')) os = 'Linux';

  let browser = 'Trình duyệt';
  if (ua.includes('Edg/')) browser = 'Microsoft Edge';
  else if (ua.includes('Chrome/')) browser = 'Google Chrome';
  else if (ua.includes('Safari/')) browser = 'Apple Safari';
  else if (ua.includes('Firefox/')) browser = 'Mozilla Firefox';

  return { name: `${browser} trên ${os}`, isMobile };
}

export default function SettingsPage() {
  const { user, updateProfile, logout } = useAuth();
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // 2FA state
  const [twoFactorStatus, setTwoFactorStatus] = useState<{ enabled: boolean; remaining_backup_codes: number } | null>(null);
  const [loading2fa, setLoading2fa] = useState(false);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [setupData, setSetupData] = useState<{ secret: string; otpauth_uri: string; backup_codes: string[] } | null>(null);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null);
  const [verifyTotpCode, setVerifyTotpCode] = useState('');
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [copiedCodes, setCopiedCodes] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [disableTotpCode, setDisableTotpCode] = useState('');

  // Sessions state
  const [sessions, setSessions] = useState<SessionItem[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);

  // Notification settings toggle
  const [notifyLoginAlert, setNotifyLoginAlert] = useState(true);
  const [notifyDocSigned, setNotifyDocSigned] = useState(true);

  const loadSecurityData = async () => {
    setLoading2fa(true);
    setLoadingSessions(true);
    try {
      const [res2fa, resSessions] = await Promise.all([
        userApi.get2faStatus(),
        userApi.getSessions(),
      ]);
      setTwoFactorStatus(res2fa.data.data);
      setSessions(resSessions.data.data || []);
    } catch (err: any) {
      console.error('Error fetching security info:', err);
    } finally {
      setLoading2fa(false);
      setLoadingSessions(false);
    }
  };

  useEffect(() => {
    loadSecurityData();
  }, []);

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await updateProfile({ full_name: fullName });
      setSuccess('Đã lưu thông tin');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Không thể cập nhật hồ sơ');
    } finally {
      setSaving(false);
    }
  };

  // Start 2FA setup in a neat modal
  const handleStart2faSetup = async () => {
    setError(null);
    setSuccess(null);
    setLoading2fa(true);
    try {
      const res = await userApi.setup2fa();
      const data = res.data.data;
      setSetupData(data);
      const qrUrl = await QRCode.toDataURL(data.otpauth_uri, {
        width: 170,
        margin: 1,
        color: {
          dark: '#0f172a',
          light: '#ffffff',
        },
      });
      setQrCodeDataUrl(qrUrl);
      setShowSetupModal(true);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Không thể tạo phiên thiết lập 2FA');
    } finally {
      setLoading2fa(false);
    }
  };

  // Confirm and activate 2FA
  const handleConfirm2fa = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!setupData || !verifyTotpCode.trim()) return;
    setError(null);
    setSuccess(null);
    setLoading2fa(true);
    try {
      await userApi.enable2fa({
        secret: setupData.secret,
        code: verifyTotpCode.trim(),
        backup_codes: setupData.backup_codes,
      });
      setSuccess('Đã kích hoạt xác thực 2 lớp (2FA) thành công');
      setShowSetupModal(false);
      setSetupData(null);
      setVerifyTotpCode('');
      await loadSecurityData();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Mã xác thực 6 số không đúng. Vui lòng kiểm tra lại.');
    } finally {
      setLoading2fa(false);
    }
  };

  // Disable 2FA
  const handleDisable2fa = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!disableTotpCode.trim()) return;
    setError(null);
    setSuccess(null);
    setLoading2fa(true);
    try {
      await userApi.disable2fa(disableTotpCode.trim());
      setSuccess('Đã tắt bảo mật 2 lớp');
      setShowDisableModal(false);
      setDisableTotpCode('');
      await loadSecurityData();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Mã xác thực không chính xác.');
    } finally {
      setLoading2fa(false);
    }
  };

  const handleCopySecret = () => {
    if (!setupData?.secret) return;
    navigator.clipboard.writeText(setupData.secret);
    setCopiedSecret(true);
    setTimeout(() => setCopiedSecret(false), 2000);
  };

  const handleCopyBackupCodes = () => {
    if (!setupData?.backup_codes) return;
    navigator.clipboard.writeText(setupData.backup_codes.join('\n'));
    setCopiedCodes(true);
    setTimeout(() => setCopiedCodes(false), 2000);
  };

  const handleDownloadBackupCodes = () => {
    if (!setupData?.backup_codes) return;
    const element = document.createElement('a');
    const file = new Blob([
      `PDF SIGN - MÃ DỰ PHÒNG 2FA: ${user?.email}\n` +
      `Ngày tạo: ${new Date().toLocaleString('vi-VN')}\n\n` +
      setupData.backup_codes.map((c, i) => `${i + 1}. ${c}`).join('\n')
    ], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `pdfsign-2fa-backup-codes.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleRevokeSession = async (sessionId: string) => {
    try {
      await userApi.revokeSession(sessionId);
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
      setSuccess('Đã thu hồi phiên làm việc');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Không thể thu hồi phiên');
    }
  };

  const handleRevokeAllSessions = async () => {
    const ok = await confirmDialog({
      title: 'Đăng xuất tất cả thiết bị',
      message: 'Bạn có chắc chắn muốn đăng xuất khỏi tất cả các thiết bị khác?',
      confirmText: 'Đăng xuất',
      cancelText: 'Hủy',
      type: 'warning',
    });
    if (!ok) return;

    try {
      await userApi.revokeAllSessions();
      toast.success('Đã đăng xuất các thiết bị khác thành công');
      await loadSecurityData();
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'Không thể thao tác');
    }
  };

  return (
    <div className="w-full h-[calc(100vh-105px)] max-h-[calc(100vh-105px)] flex flex-col pb-[30px] overflow-hidden">
      {/* Top Header Row */}
      <div className="flex items-center justify-between pb-3 flex-shrink-0">
        <div>
          <h1 className="text-lg font-bold tracking-tight text-slate-900 dark:text-white">
            Cài đặt & Quản lý phiên
          </h1>
          <p className="text-[11px] text-slate-500 dark:text-slate-400">
            Bảo mật xác thực 2 lớp (2FA), cảnh báo email và kiểm soát các thiết bị truy cập
          </p>
        </div>

        {/* Status notification toast */}
        <div className="flex items-center gap-2">
          {error && (
            <div className="py-1 px-3 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-500/20 rounded-lg flex items-center gap-1.5 text-rose-600 dark:text-rose-400 text-xs animate-fade-in">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
          {success && (
            <div className="py-1 px-3 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-500/20 rounded-lg flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400 text-xs animate-fade-in">
              <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" />
              <span>{success}</span>
            </div>
          )}
        </div>
      </div>

      {/* Main 2-Column Layout - Perfectly fits viewport without whole-page scroll */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-5 min-h-0 overflow-hidden">
        
        {/* LEFT COLUMN: Sidebar của trang Cài đặt (Hồ sơ, Thông báo + 2 Card 2FA & Cảnh báo Email) */}
        <div className="lg:col-span-5 flex flex-col gap-3 min-h-0 overflow-y-auto pr-1">
          
          {/* Section 1: Hồ sơ & Thông báo */}
          <div className="p-4 bg-white dark:bg-[#111827] rounded-xl border border-slate-200/80 dark:border-white/10 shadow-sm space-y-3 flex-shrink-0">
            <div className="flex items-center justify-between pb-2 border-b border-slate-100 dark:border-white/5">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-sky-500 text-white font-bold flex items-center justify-center text-xs shadow-sm">
                  {user?.full_name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-900 dark:text-white leading-tight">
                    {user?.full_name || 'Admin'}
                  </p>
                  <p className="text-[11px] text-slate-400 leading-tight">{user?.email}</p>
                </div>
              </div>
              <span className="px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-slate-100 text-slate-600 dark:bg-white/5 dark:text-slate-300">
                {user?.role || 'member'}
              </span>
            </div>

            {/* Notification settings checklist */}
            <div className="space-y-1.5 pt-0.5">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-600 dark:text-slate-300">
                <Bell className="w-3.5 h-3.5 text-slate-400" />
                <span>Tùy chọn nhận thông báo qua email:</span>
              </div>
              <label className="flex items-center gap-2 cursor-pointer text-[11px] text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200">
                <input
                  type="checkbox"
                  checked={notifyDocSigned}
                  onChange={(e) => setNotifyDocSigned(e.target.checked)}
                  className="rounded border-slate-300 text-sky-600 focus:ring-sky-500 w-3.5 h-3.5"
                />
                <span>Email khi tài liệu được ký hoàn tất hoặc có tài liệu mới</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-[11px] text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200">
                <input
                  type="checkbox"
                  checked={notifyLoginAlert}
                  onChange={(e) => setNotifyLoginAlert(e.target.checked)}
                  className="rounded border-slate-300 text-sky-600 focus:ring-sky-500 w-3.5 h-3.5"
                />
                <span>Cảnh báo bảo mật khi có đăng nhập từ vị trí mới</span>
              </label>
            </div>
          </div>

          {/* Section 2: Xác thực 2 bước (2FA - TOTP) */}
          <div className="p-4 bg-white dark:bg-[#111827] rounded-xl border border-slate-200/80 dark:border-white/10 shadow-sm space-y-2.5 flex-shrink-0">
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-0.5">
                <div className="flex items-center gap-1.5">
                  <KeyRound className="w-3.5 h-3.5 text-slate-400" />
                  <h3 className="text-xs font-bold text-slate-900 dark:text-white">
                    Xác thực 2 bước (2FA - TOTP)
                  </h3>
                  {twoFactorStatus?.enabled ? (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20">
                      <ShieldCheck className="w-3 h-3" /> Đã bật
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-600 dark:bg-white/5 dark:text-slate-400 border border-slate-200 dark:border-white/10">
                      Chưa kích hoạt
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                  Dùng mã 6 số từ Google Authenticator hoặc Microsoft Authenticator khi đăng nhập
                </p>
              </div>

              {!twoFactorStatus?.enabled ? (
                <button
                  onClick={handleStart2faSetup}
                  disabled={loading2fa}
                  className="btn-primary text-xs py-1.5 px-3 flex items-center gap-1.5 flex-shrink-0 shadow-sm"
                >
                  {loading2fa ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Smartphone className="w-3.5 h-3.5" />}
                  Bật 2FA
                </button>
              ) : (
                <button
                  onClick={() => setShowDisableModal(true)}
                  className="px-2.5 py-1 rounded-lg border border-rose-200 dark:border-rose-500/20 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/30 text-xs font-medium transition-colors flex-shrink-0"
                >
                  Tắt 2FA
                </button>
              )}
            </div>

            {twoFactorStatus?.enabled && (
              <div className="pt-2 border-t border-slate-100 dark:border-white/5 flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400">
                <span>Mã dự phòng: <strong className="text-slate-800 dark:text-slate-200">{twoFactorStatus.remaining_backup_codes}</strong>/8 mã</span>
                <span className="text-[10px] text-slate-400">RFC 6238 TOTP</span>
              </div>
            )}
          </div>

          {/* Section 3: Cảnh báo đăng nhập mới qua Email */}
          <div className="p-4 bg-white dark:bg-[#111827] rounded-xl border border-slate-200/80 dark:border-white/10 shadow-sm space-y-1.5 flex-shrink-0">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5">
                <MailCheck className="w-3.5 h-3.5 text-slate-400" />
                <h3 className="text-xs font-bold text-slate-900 dark:text-white">
                  Cảnh báo đăng nhập mới qua Email
                </h3>
              </div>
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20 flex-shrink-0">
                <Check className="w-3 h-3" /> Đang bảo vệ
              </span>
            </div>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
              Tự động gửi email cảnh báo chi tiết (thời gian, địa chỉ IP, thiết bị) kèm nút thu hồi quyền truy cập khi có thiết bị mới đăng nhập.
            </p>
          </div>
        </div>

        {/* RIGHT COLUMN: Toàn bộ khu vực dành riêng cho Phiên đăng nhập & Thiết bị đang hoạt động */}
        <div className="lg:col-span-7 flex flex-col bg-white dark:bg-[#111827] rounded-xl border border-slate-200/80 dark:border-white/10 shadow-sm p-4 min-h-0 overflow-hidden">
          
          {/* Right Header */}
          <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-white/5 flex-shrink-0">
            <div className="flex items-center gap-2">
              <Laptop className="w-4 h-4 text-sky-500" />
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-xs font-bold text-slate-900 dark:text-white">
                    Phiên đăng nhập & Thiết bị đang hoạt động
                  </h2>
                  <span className="px-1.5 py-0.2 rounded text-[10px] font-medium bg-slate-100 text-slate-600 dark:bg-white/5 dark:text-slate-400">
                    {sessions.length} phiên
                  </span>
                </div>
                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                  Danh sách trình duyệt và thiết bị hiện có quyền truy cập vào tài khoản
                </p>
              </div>
            </div>

            {sessions.length > 1 && (
              <button
                onClick={handleRevokeAllSessions}
                className="text-xs text-rose-500 hover:text-rose-600 font-medium transition-colors flex items-center gap-1"
                title="Đăng xuất khỏi tất cả các thiết bị khác"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>Đăng xuất thiết bị khác</span>
              </button>
            )}
          </div>

          {/* Dedicated Scrollable Session List (Thanh cuộn riêng, không dùng thanh cuộn trang) */}
          <div className="flex-1 overflow-y-auto space-y-2 pr-1.5 my-2.5 min-h-0">
            {loadingSessions ? (
              <div className="h-full flex items-center justify-center text-xs text-slate-400 gap-2 py-8">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Đang tải danh sách phiên đăng nhập...</span>
              </div>
            ) : sessions.length === 0 ? (
              <div className="h-full flex items-center justify-center text-xs text-slate-400 py-8">
                Không tìm thấy phiên làm việc nào.
              </div>
            ) : (
              sessions.map((sess, idx) => {
                const info = parseUserAgent(sess.user_agent);
                const isCurrent = idx === 0;
                return (
                  <div
                    key={sess.id}
                    className={`p-3 rounded-lg border transition-colors flex items-center justify-between gap-3 ${
                      isCurrent
                        ? 'bg-sky-50/50 dark:bg-sky-950/20 border-sky-200 dark:border-sky-500/20'
                        : 'bg-slate-50/60 dark:bg-white/[0.02] border-slate-200/80 dark:border-white/5 hover:border-slate-300 dark:hover:border-white/10'
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        isCurrent
                          ? 'bg-sky-100 dark:bg-sky-500/20 text-sky-600 dark:text-sky-400'
                          : 'bg-slate-100 dark:bg-white/5 text-slate-500'
                      }`}>
                        {info.isMobile ? <Smartphone className="w-4 h-4" /> : <Laptop className="w-4 h-4" />}
                      </div>

                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-xs font-semibold text-slate-800 dark:text-slate-200 truncate">
                            {info.name}
                          </p>
                          {isCurrent && (
                            <span className="px-1.5 py-0.2 rounded text-[9px] font-bold bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-300 border border-sky-200 dark:border-sky-500/20 flex-shrink-0">
                              Phiên hiện tại
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-slate-400 truncate mt-0.5">
                          IP: <span className="font-mono">{sess.ip_address || '127.0.0.1'}</span> • Đăng nhập: {new Date(sess.created_at).toLocaleString('vi-VN')}
                        </p>
                      </div>
                    </div>

                    {!isCurrent && (
                      <button
                        onClick={() => handleRevokeSession(sess.id)}
                        className="p-1.5 text-slate-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded-lg transition-colors flex-shrink-0"
                        title="Hủy phiên làm việc này"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Right Column Footer - Logout Button */}
          <div className="pt-2.5 border-t border-slate-100 dark:border-white/5 flex items-center justify-between flex-shrink-0">
            <span className="text-[11px] text-slate-400">Rời khỏi hệ thống trên trình duyệt này:</span>
            <button
              onClick={async () => {
                await logout();
                window.location.href = '/login';
              }}
              className="text-xs text-rose-500 hover:text-rose-600 font-medium flex items-center gap-1.5 py-1 px-2 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-950/20 transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span>Đăng xuất ngay</span>
            </button>
          </div>
        </div>

      </div>

      {/* MODAL 2FA SETUP (Popup tập trung) */}
      {showSetupModal && setupData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-[#111827] rounded-xl border border-slate-200 dark:border-white/10 shadow-2xl p-5 max-w-md w-full space-y-4">
            <div className="flex items-center justify-between pb-2 border-b border-slate-100 dark:border-white/5">
              <div className="flex items-center gap-2">
                <Smartphone className="w-4 h-4 text-sky-500" />
                <h3 className="text-sm font-bold text-slate-900 dark:text-white">Thiết lập 2FA Authenticator</h3>
              </div>
              <button
                onClick={() => setShowSetupModal(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* QR + Key */}
            <div className="flex flex-col items-center gap-3">
              <div className="p-2 bg-white rounded-lg border border-slate-200 shadow-inner">
                {qrCodeDataUrl ? (
                  <img src={qrCodeDataUrl} alt="2FA QR Code" className="w-40 h-40" />
                ) : (
                  <div className="w-40 h-40 flex items-center justify-center">
                    <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                  </div>
                )}
              </div>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 text-center">
                Mở Google Authenticator / Microsoft Authenticator và quét mã QR trên
              </p>

              <div className="w-full flex items-center gap-1.5 p-1.5 bg-slate-50 dark:bg-white/5 rounded-lg border border-slate-200/80 dark:border-white/10">
                <span className="text-[10px] text-slate-400 pl-1">Khóa:</span>
                <span className="text-xs font-mono font-bold text-slate-700 dark:text-slate-300 flex-1 truncate">
                  {setupData.secret}
                </span>
                <button
                  type="button"
                  onClick={handleCopySecret}
                  className="px-2 py-1 rounded bg-white dark:bg-white/10 text-[10px] font-medium text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-white/5 hover:bg-slate-50"
                >
                  {copiedSecret ? 'Đã chép' : 'Sao chép'}
                </button>
              </div>
            </div>

            {/* Backup codes preview */}
            <div className="p-2.5 rounded-lg bg-slate-50 dark:bg-white/5 border border-slate-200/80 dark:border-white/5 space-y-1.5">
              <div className="flex items-center justify-between text-[11px]">
                <span className="font-semibold text-slate-700 dark:text-slate-300">Mã dự phòng (8 mã)</span>
                <div className="flex items-center gap-2">
                  <button onClick={handleCopyBackupCodes} className="text-sky-500 hover:underline">
                    {copiedCodes ? 'Đã chép' : 'Chép'}
                  </button>
                  <button onClick={handleDownloadBackupCodes} className="text-sky-500 hover:underline flex items-center gap-0.5">
                    <Download className="w-3 h-3" /> Tải về
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-1 font-mono text-[10px] text-center text-slate-600 dark:text-slate-400">
                {setupData.backup_codes.map((c, i) => (
                  <span key={i} className="bg-white dark:bg-white/5 py-0.5 rounded">{c}</span>
                ))}
              </div>
            </div>

            {/* Verification Form */}
            <form onSubmit={handleConfirm2fa} className="space-y-3 pt-2">
              <div>
                <label className="text-[11px] font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Nhập mã 6 chữ số từ ứng dụng để hoàn tất:
                </label>
                <input
                  type="text"
                  maxLength={6}
                  value={verifyTotpCode}
                  onChange={(e) => setVerifyTotpCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  className="input font-mono tracking-widest text-center text-lg h-10 w-full"
                  autoFocus
                />
              </div>
              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setShowSetupModal(false)}
                  className="btn-ghost text-xs"
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  disabled={loading2fa || verifyTotpCode.length !== 6}
                  className="btn-primary text-xs px-4"
                >
                  {loading2fa ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Xác nhận & Bật'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL DISABLE 2FA */}
      {showDisableModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-[#111827] rounded-xl border border-slate-200 dark:border-white/10 shadow-2xl p-5 max-w-sm w-full space-y-3">
            <div className="flex items-center gap-2 text-rose-500">
              <ShieldAlert className="w-4 h-4" />
              <h3 className="text-sm font-bold text-slate-900 dark:text-white">Tắt xác thực 2FA</h3>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Nhập mã 6 số từ ứng dụng trên điện thoại để xác nhận tắt tính năng bảo vệ 2FA:
            </p>
            <form onSubmit={handleDisable2fa} className="space-y-3">
              <input
                type="text"
                maxLength={6}
                value={disableTotpCode}
                onChange={(e) => setDisableTotpCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                className="input font-mono tracking-widest text-center text-lg"
                autoFocus
              />
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowDisableModal(false);
                    setDisableTotpCode('');
                  }}
                  className="btn-ghost text-xs"
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  disabled={loading2fa || disableTotpCode.length !== 6}
                  className="btn-danger text-xs px-3"
                >
                  {loading2fa ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Xác nhận tắt'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}